import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  GetAllChamberMembersQuery,
  ShopLocalCampaignPrizeDrawParticipantsReturn,
  ShopLocalCampaignReturn,
  ShopLocalCampaignStatusEnum,
} from "../../../graphql/types";
import { getLocalTimestamp } from "../Root/RootAdmin";
import { Button, Chip, makeStyles, Modal, useMediaQuery, Menu, MenuItem, TextField, InputAdornment, IconButton } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import theme from "../../../themes";
import { EndCampaignModal } from "./updateChamberShopLocalCampaign";
import { CSVLink } from "react-csv";
import CommonTable, { HeadCell } from "../../common/CommonTable";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SearchIcon from "@material-ui/icons/Search";

type OmittedRedeemSettingsShopLocalCampaign = ShopLocalCampaignReturn;
type AllShopLocalCampaignDataType =
  | ShopLocalCampaignReturn[]
  | undefined
  | null;
type TableShopLocalCampaignDataType = OmittedRedeemSettingsShopLocalCampaign;
type AllTableShopLocalCampaignDataType = TableShopLocalCampaignDataType[];


const useStyles = makeStyles(() => ({
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    height: "700px",
    borderRadius: "5px",
    textAlign: "center",
    width: "740px",
    maxWidth: "100%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: "#cfd8dc",
    padding: theme.spacing(4),
    position: "relative",
  },
  modalButton: {
    margin: "10px",
    marginTop: "20px",
    width: "230px",
    backgroundColor: "#E1A731",
    color: "#e9e9e9",
    fontWeight: "bold",
    height: "40px",
  },
  // ── Mobile list view ──────────────────────────────────────────────
  mobileList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    width: "100%",
  },
  mobileSearchBar: {
    width: "100%",
    marginBottom: "4px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#FFFFFF",
      "& fieldset": { borderColor: "#E0E0E0" },
      "&.Mui-focused fieldset": { borderColor: "#000", borderWidth: "1.5px" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 10px",
      fontSize: "14px",
    },
  },
  mobileCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "14px",
    padding: "14px",
    color: "#24272C",
    fontFamily: "DM Sans, sans-serif",
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    border: "1px solid #E8E8E8",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  mobileCardTop: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  mobileAvatar: {
    width: "40px",
    height: "40px",
    minWidth: "40px",
    borderRadius: "50%",
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  mobileTitles: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  mobileCampaignTitle: {
    fontWeight: 700,
    fontSize: "15px",
    color: "#24272C",
    lineHeight: 1.25,
    wordBreak: "break-word" as const,
  },
  mobileCampaignSubtitle: {
    fontWeight: 400,
    fontSize: "13px",
    color: "#666",
    lineHeight: 1.3,
    wordBreak: "break-word" as const,
  },
  mobileStatusPill: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
    color: "#888",
    whiteSpace: "nowrap" as const,
    alignSelf: "flex-start" as const,
    marginTop: "2px",
  },
  mobileStatusPillActive: { color: "#2E7D32" },
  mobileStatusPillUpcoming: { color: "#E1A731" },
  mobileMenuBtn: {
    color: "#666",
    padding: "2px",
    marginLeft: "2px",
  },
  mobileDivider: {
    height: "1px",
    backgroundColor: "#EEE",
    width: "100%",
  },
  mobileMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "13px",
  },
  mobileMetaLabel: {
    color: "#666",
    fontWeight: 500,
  },
  mobileMetaValue: {
    color: "#24272C",
    fontWeight: 600,
    textAlign: "right" as const,
    wordBreak: "break-word" as const,
  },
  mobileBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  mobileCountNumber: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#24272C",
    lineHeight: 1,
  },
  mobileCountLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "2px",
  },
  mobileViewUsersBtn: {
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    border: "none",
    borderRadius: "10px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "none" as const,
    whiteSpace: "nowrap" as const,
    "&:hover": { backgroundColor: "#C8922A" },
  },
  mobileEmpty: {
    color: "#666",
    textAlign: "center" as const,
    padding: "40px 0",
    fontSize: "14px",
  },
}));

// Reusable table component for modal displays
interface ReusableTableProps<T> {
  data: T[];
  columns: {
    id: string;
    label: string;
    renderer: (item: T) => React.ReactNode;
    align?: "left" | "right" | "center";
  }[];
}

function ReusableTable<T>({ data, columns }: ReusableTableProps<T>) {
  return (
    <div style={{ height: "100%", overflow: "hidden" }}>
      <Paper
        style={{
          borderRadius: "8px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          overflow: "auto",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    padding: "16px 24px",
                    borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                    textAlign: column.align || "left",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                key={index}
                style={{
                  backgroundColor: "#ffffff",
                  borderBottom: "1px solid rgba(224, 224, 224, 0.3)",
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    style={{
                      fontSize: "16px",
                      padding: "16px 24px",
                      color: "rgba(0, 0, 0, 0.87)",
                      borderBottom: "1px solid rgba(168, 168, 168, 0.3)",
                      textAlign: column.align || "left",
                    }}
                  >
                    {column.renderer(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
}

export default function FullPageShopLocalCampaignsTable({
  allShopLocalCampaignData,
  allChamberMemberData,
  mode,
}: {
  allChamberMemberData: GetAllChamberMembersQuery | undefined;
  allShopLocalCampaignData: AllShopLocalCampaignDataType;
  mode: "active" | "inactive";
}) {
  const classes = useStyles();
  const history = useHistory();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const [mobileSearch, setMobileSearch] = React.useState("");
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [mobileMenuCampaignId, setMobileMenuCampaignId] = React.useState<string | null>(null);

  const getInitials = (name: string) => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    return words.slice(0, 3).map(w => w[0]).join("").toUpperCase();
  };
  const formatShortDate = (ms: number) => {
    if (!ms) return "-";
    try {
      return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "-";
    }
  };

  // Column definitions for CommonTable
  const columns: HeadCell[] = React.useMemo(
    () => [
      {
        id: "campaignName",
        label: enumStrings.fullPageShopLocal.contestName,
      },
      {
        id: "prizeDescription",
        label: enumStrings.fullPageShopLocal.prizeDescription,
      },
      {
        id: "startDateUtcMilli",
        label: enumStrings.fullPageShopLocal.startDate,
        preventTextWrap: true,
      },
      {
        id: "endDateUtcMilli",
        label: enumStrings.fullPageShopLocal.endDate,
        preventTextWrap: true,
      },
      {
        id: "participatingMembers",
        label: enumStrings.fullPageShopLocal.participatingMembers,
        sortable: false,
        alignCenter: true,
      },
      {
        id: "prizeDrawParticipants",
        label: enumStrings.fullPageShopLocal.prizeDrawParticipants,
        sortable: false,
        alignCenter: true,
      },
      {
        id: "status",
        label: enumStrings.fullPageShopLocal.status,
        alignCenter: true,
      },
      {
        id: "updateActiveCampaign",
        label: "",
        sortable: false,
      },
    ],
    [enumStrings]
  );

  // Transform data for table
  const allTableShopLocalCampaignData: AllTableShopLocalCampaignDataType =
    React.useMemo(() => {
      if (!allShopLocalCampaignData) {
        return [];
      }

      const allShopLocalCampaignsArr = (allShopLocalCampaignData || []).map(
        (shopLocalCampaignData) => ({
          ...shopLocalCampaignData,
        })
      );

      if (mode === "active") {
        return allShopLocalCampaignsArr.filter(
          (campaign) => campaign.status === ShopLocalCampaignStatusEnum.Active
        );
      }

      return allShopLocalCampaignsArr.filter(
        (campaign) => campaign.status === ShopLocalCampaignStatusEnum.Inactive
      );
    }, [allShopLocalCampaignData, mode]);

  const [memberModalOpenedForCampaignsId, setMemberModalOpenedForCampaignsId] =
    React.useState<string>("");

  const [memberModalOpenedIsActive, setMemberModalOpenedIsActive] =
    React.useState<boolean>(false);

  const [participatingMembersModalOpen, setParticipatingMembersModalOpen] =
    React.useState<boolean>(false);

  const [participantModalOpen, setParticipantModalOpen] =
    React.useState<boolean>(false);

  const [randomWinnerModalOpen, setRandomWinnerModalOpen] =
    React.useState<boolean>(false);
  const [randomWinnerEmail, setRandomWinnerEmail] = React.useState<string>("");

  // Add state for sorted members
  const [sortedMembersData, setSortedMembersData] = React.useState<
    Array<{
      memberName: string;
      numberOfParticipants: number;
      participationCode: string;
    }>
  >([]);

  // Helper function to get participant emails for a member
  const getParticipantEmailsForMember = React.useCallback(
    (memberParticipationCode: string): string[] => {
      if (!memberParticipationCode || !memberModalOpenedForCampaignsId) {
        return [];
      }

      const participantEmails: string[] = [];

      for (const campaign of allShopLocalCampaignData || []) {
        if (campaign?.id === memberModalOpenedForCampaignsId) {
          const participatingMembers = campaign.participatingMembers || [];
          const prizeDrawParticipants = campaign.prizeDrawParticipants || [];

          const memberId =
            participatingMembers.find(
              (m) => m?.participationCode === memberParticipationCode
            )?.memberId || "";

          if (!memberId) {
            continue;
          }

          for (const participant of prizeDrawParticipants) {
            const redeemedIds = participant?.redeemedAtMemberIds || [];
            // Check if this participant redeemed at this member
            if (redeemedIds.includes(memberId)) {
              if (participant?.userEmail) {
                participantEmails.push(participant.userEmail);
              }
            }
          }
          break;
        }
      }

      return participantEmails;
    },
    [allShopLocalCampaignData, memberModalOpenedForCampaignsId]
  );

  const participatingMemberCompaniesTable = React.useMemo(() => {
    if (
      memberModalOpenedForCampaignsId.length > 0 &&
      (allShopLocalCampaignData || []).length > 0 &&
      (allChamberMemberData?.getAllChamberMembers || []).length > 0
    ) {
      // create object for member id to number of participants
      const participatingMembersMap: Record<string, number> = {};
      for (const campaign of allShopLocalCampaignData || []) {
        if (campaign.id === memberModalOpenedForCampaignsId) {
          for (const participantObj of campaign.prizeDrawParticipants || []) {
            for (const memberId of participantObj.redeemedAtMemberIds || []) {
              if (participatingMembersMap[memberId]) {
                participatingMembersMap[memberId] += 1;
              } else {
                participatingMembersMap[memberId] = 1;
              }
            }
          }
          break;
        }
      }

      const listOfParticipatingMembers: {
        memberName: string;
        numberOfParticipants: number;
        participationCode: string;
      }[] = [];

      for (const campaign of allShopLocalCampaignData || []) {
        if (campaign.id === memberModalOpenedForCampaignsId) {
          for (const participatingMemebrObj of campaign.participatingMembers) {
            for (const realMember of allChamberMemberData?.getAllChamberMembers ||
              []) {
              if (participatingMemebrObj.memberId === realMember.id) {
                listOfParticipatingMembers.push({
                  memberName: realMember.name,
                  numberOfParticipants:
                    participatingMembersMap[realMember.id] || 0,
                  participationCode: participatingMemebrObj.participationCode,
                });
                continue;
              }
            }
          }
          break;
        }
      }

      // 1. Filter and sort members with numberOfParticipants > 0 alphabetically
      const positiveParticipants = listOfParticipatingMembers
        .filter((member) => member.numberOfParticipants > 0)
        .sort((a, b) => a.memberName.localeCompare(b.memberName));

      // 2. Filter and sort members with numberOfParticipants === 0 alphabetically
      const zeroParticipants = listOfParticipatingMembers
        .filter((member) => member.numberOfParticipants === 0)
        .sort((a, b) => a.memberName.localeCompare(b.memberName));

      // 3. Join the two lists
      const sortedMembers = [...positiveParticipants, ...zeroParticipants];

      // Store sortedMembers in state for CSV export
      setSortedMembersData(sortedMembers);

      if (sortedMembers.length === 0) {
        return <div>{enumStrings.fullPageShopLocal.noneSoFar}</div>;
      }

      return (
        <ReusableTable
          data={sortedMembers}
          columns={[
            {
              id: "memberName",
              label:
                enumStrings.shopLocal.fullPageShopLocalCampaign
                  .memberCompanyName,
              renderer: (member) => member.memberName,
            },
            {
              id: "participationCode",
              label:
                enumStrings.shopLocal.fullPageShopLocalCampaign
                  .participatingCode,
              renderer: (member) => member.participationCode,
            },
            {
              id: "numberOfParticipants",
              label:
                enumStrings.shopLocal.fullPageShopLocalCampaign
                  .usersInPrizeDraw,
              renderer: (member) => {
                // If there are no participants, just show 0
                if (member.numberOfParticipants === 0) {
                  return 0;
                }

                // Get participant emails and render as a bullet list
                const participantEmails = getParticipantEmailsForMember(
                  member.participationCode
                );

                // Return a bullet point list of emails
                return (
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      textAlign: "left",
                    }}
                  >
                    <li key="total">
                      <strong>Total {participantEmails.length} users</strong>
                    </li>
                    {participantEmails.map((email) => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                );
              },
            },
          ]}
        />
      );
    }
    return null;
  }, [
    allChamberMemberData?.getAllChamberMembers,
    allShopLocalCampaignData,
    enumStrings.fullPageShopLocal.noneSoFar,
    enumStrings.shopLocal.fullPageShopLocalCampaign.memberCompanyName,
    enumStrings.shopLocal.fullPageShopLocalCampaign.participatingCode,
    enumStrings.shopLocal.fullPageShopLocalCampaign.usersInPrizeDraw,
    memberModalOpenedForCampaignsId,
    getParticipantEmailsForMember,
  ]);

  // Add a new useCallback for sorting prize draw participants
  const sortPrizeDrawParticipantsByRedemptions = React.useCallback(
    (participants: ShopLocalCampaignPrizeDrawParticipantsReturn[]) => {
      if (participants.length === 0) return participants;

      return [...participants].sort(
        (a, b) => b.redeemedAtMemberIds.length - a.redeemedAtMemberIds.length
      );
    },
    []
  );

  const cpaUsersParticipatingInDrawList = React.useMemo(() => {
    if (
      memberModalOpenedForCampaignsId.length > 0 &&
      (allShopLocalCampaignData || []).length > 0 &&
      (allChamberMemberData?.getAllChamberMembers || []).length > 0
    ) {
      let prizeDrawParticipantArr: ShopLocalCampaignPrizeDrawParticipantsReturn[] =
        [];
      for (const campaign of allShopLocalCampaignData || []) {
        if (campaign.id === memberModalOpenedForCampaignsId) {
          prizeDrawParticipantArr = campaign.prizeDrawParticipants || [];
          break;
        }
      }
      if (prizeDrawParticipantArr.length > 0) {
        // Sort participants using the callback
        prizeDrawParticipantArr = sortPrizeDrawParticipantsByRedemptions(
          prizeDrawParticipantArr
        );
      }

      let memberIdsToPullNamesFor: string[] = [];

      for (const participantObj of prizeDrawParticipantArr) {
        for (const memberId of participantObj.redeemedAtMemberIds || []) {
          memberIdsToPullNamesFor.push(memberId);
        }
      }

      // @ts-ignore
      memberIdsToPullNamesFor = [...new Set(memberIdsToPullNamesFor)];

      const memberIdToName: {
        [key: string]: string;
      } = {};
      for (const id of memberIdsToPullNamesFor) {
        for (const member of allChamberMemberData?.getAllChamberMembers || []) {
          if (member.id === id) {
            memberIdToName[id] = member.name;
          }
        }
      }

      if (prizeDrawParticipantArr.length === 0) {
        return <div>{enumStrings.fullPageShopLocal.noneSoFar}</div>;
      }

      return (
        <ReusableTable
          data={prizeDrawParticipantArr}
          columns={[
            {
              id: "userEmail",
              label: enumStrings.fullPageShopLocal.EntrantEmail,
              renderer: (participant) => participant.userEmail,
            },
            {
              id: "redeemedAtMemberIds",
              label: enumStrings.fullPageShopLocal.enteredPize,
              renderer: (participant) => (
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {participant.redeemedAtMemberIds.map((memberId) => (
                    <li key={memberId}>{memberIdToName[memberId]}</li>
                  ))}
                </ul>
              ),
            },
          ]}
        />
      );
    }

    return <div>{enumStrings.fullPageShopLocal.noneSoFar}</div>;
  }, [
    allChamberMemberData?.getAllChamberMembers,
    allShopLocalCampaignData,
    enumStrings.fullPageShopLocal.EntrantEmail,
    enumStrings.fullPageShopLocal.enteredPize,
    enumStrings.fullPageShopLocal.noneSoFar,
    memberModalOpenedForCampaignsId,
    sortPrizeDrawParticipantsByRedemptions,
  ]);

  const setRandomWinnerEmailFunc = React.useCallback(() => {
    if (
      memberModalOpenedForCampaignsId.length > 0 &&
      (allShopLocalCampaignData || []).length > 0 &&
      (allChamberMemberData?.getAllChamberMembers || []).length > 0
    ) {
      let prizeDrawParticipantArr: ShopLocalCampaignPrizeDrawParticipantsReturn[] =
        [];
      for (const campaign of allShopLocalCampaignData || []) {
        if (campaign.id === memberModalOpenedForCampaignsId) {
          prizeDrawParticipantArr = campaign.prizeDrawParticipants || [];
          break;
        }
      }

      if (prizeDrawParticipantArr.length > 0) {
        // create weighted array to favour entrants with higher number of member perks redeemed
        const weightedArray: {
          userEmail: string;
          redeemedAtMemberIds: string[];
        }[] = [];

        for (const entry of prizeDrawParticipantArr) {
          for (let i = 0; i < (entry.redeemedAtMemberIds || []).length; i++) {
            weightedArray.push({
              userEmail: entry.userEmail,
              redeemedAtMemberIds: entry.redeemedAtMemberIds,
            });
          }
        }

        // set random winner emial
        const randomWinner =
          weightedArray[Math.floor(Math.random() * weightedArray.length)];
        setRandomWinnerEmail(
          `${randomWinner.userEmail}\n\n(${enumStrings.fullPageShopLocal.description2} ${randomWinner.redeemedAtMemberIds.length} ${enumStrings.fullPageShopLocal.description3})`
        );
      } else {
        setRandomWinnerEmail("");
      }
    }
  }, [
    allChamberMemberData?.getAllChamberMembers,
    allShopLocalCampaignData,
    enumStrings.fullPageShopLocal.description2,
    enumStrings.fullPageShopLocal.description3,
    memberModalOpenedForCampaignsId,
  ]);

  const [endCampaignModalOpen, setEndCampaignModalOpen] = React.useState(false);
  const [endModalCampaignId, setEndModalCampaignId] = React.useState("");

  // Custom cell renderer
  const renderCell = React.useCallback(
    (column: HeadCell, row: TableShopLocalCampaignDataType) => {
      const isActive = row.status === ShopLocalCampaignStatusEnum.Active;
      const isUpcoming = isActive && Date.now() < row.startDateUtcMilli;

      switch (column.id) {
        case "campaignName":
          return row.campaignName;

        case "prizeDescription":
          return row.prizeDescription;

        case "startDateUtcMilli":
          return getLocalTimestamp(row.startDateUtcMilli || 0, true);

        case "endDateUtcMilli":
          return getLocalTimestamp(row.endDateUtcMilli || 0, true);

        case "participatingMembers":
          return (
            <div style={{ textAlign: "center" }}>
              {row.participatingMembers.length}
              <br />
              <br />
              {row.participatingMembers.length > 0 && (
                <Button
                  className={classes.modalButton}
                  style={{
                    backgroundColor: "#37474f",
                    color: "#e9e9e9",
                    width: "100px",
                    height: "30px",
                    fontSize: "11.5px",
                    margin: 0,
                    padding: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setParticipatingMembersModalOpen(true);
                    setMemberModalOpenedIsActive(isActive);
                    setMemberModalOpenedForCampaignsId(row.id);
                  }}
                  variant="contained"
                >
                  {enumStrings.shopLocal.fullPageShopLocalCampaign.openList}
                </Button>
              )}
            </div>
          );

        case "prizeDrawParticipants":
          return (
            <div style={{ textAlign: "center" }}>
              {(row?.prizeDrawParticipants || []).length}
              <br />
              <br />
              {(row?.prizeDrawParticipants || []).length > 0 && (
                <Button
                  className={classes.modalButton}
                  style={{
                    backgroundColor: "#37474f",
                    color: "#e9e9e9",
                    width: "120px",
                    height: "50px",
                    fontSize: "11.5px",
                    margin: 0,
                    padding: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMemberModalOpenedForCampaignsId(row.id);
                    setParticipantModalOpen(true);
                  }}
                  variant="contained"
                >
                  {
                    enumStrings.shopLocal.fullPageShopLocalCampaign
                      .openListAndPickWinner
                  }
                </Button>
              )}
            </div>
          );

        case "status":
          return isActive ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Chip
                label={
                  isUpcoming
                    ? enumStrings.shopLocal.fullPageShopLocalCampaign.upcoming
                    : enumStrings.shopLocal.fullPageShopLocalCampaign.active
                }
                variant="outlined"
                size="medium"
                style={{
                  color: isUpcoming ? "#525100" : "#34694F",
                  backgroundColor: isUpcoming ? "#FEF9E8" : "#DCEEDC",
                  borderColor: isUpcoming ? "#525100" : "#34694F",
                }}
              />
              <Button
                className={classes.modalButton}
                style={{
                  backgroundColor: "#37474f",
                  color: "#e9e9e9",
                  width: "70px",
                  height: "30px",
                  fontSize: "11.5px",
                  margin: 0,
                  padding: 0,
                  marginTop: 10,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEndCampaignModalOpen(() => {
                    setEndModalCampaignId(row.id);
                    return true;
                  });
                }}
                variant="contained"
              >
                {enumStrings.shopLocal.fullPageShopLocalCampaign.end}
              </Button>
            </div>
          ) : (
            <Chip
              label={enumStrings.shopLocal.fullPageShopLocalCampaign.ended}
              variant="outlined"
              size="medium"
              style={{
                color: "#727272",
                backgroundColor: "#E3E4E6",
                borderColor: "#727272",
              }}
            />
          );

        case "updateActiveCampaign":
          return isActive ? (
            <Button
              type="submit"
              size="large"
              color="primary"
              variant="contained"
              style={{
                backgroundColor: "#E1A731",
                fontSize: "15px",
                fontWeight: "bold",
                borderRadius: "10px",
                width: "90px",
                height: "40px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                history.push(RedirectUri.updateShopLocal, {
                  shopLocalCampaignId: row.id,
                });
              }}
            >
              {enumStrings.shopLocal.fullPageShopLocalCampaign.edit}
            </Button>
          ) : null;

        default:
          return null;
      }
    },
    [classes.modalButton, enumStrings, history]
  );

  // Handle row click for navigation
  const handleRowClick = React.useCallback(
    (row: TableShopLocalCampaignDataType) => {
      if (mode === "active") {
        history.push(RedirectUri.updateShopLocal, {
          shopLocalCampaignId: row.id,
        });
      }
    },
    [history, mode]
  );

  // Empty state message
  const emptyMessage =
    mode === "active"
      ? enumStrings.shopLocal.fullPageShopLocalCampaign.noActiveContests
      : enumStrings.shopLocal.fullPageShopLocalCampaign.noPreviousContests;

  return (
    <>
      {endCampaignModalOpen && (
        <EndCampaignModal
          campaignId={endModalCampaignId}
          closeModal={() => setEndCampaignModalOpen(false)}
        />
      )}
      {participatingMembersModalOpen && (
        <Modal className={classes.modal} open={participatingMembersModalOpen}>
          <div
            className={classes.paper}
            style={{ justifyContent: "space-between", height: "800px" }}
          >
            <h1 style={{ fontWeight: "bold" }}>
              {
                enumStrings.shopLocal.fullPageShopLocalCampaign
                  .participatingMemberCompanies
              }
            </h1>
            <h3 style={{ paddingLeft: 10, paddingRight: 10 }}>
              {enumStrings.fullPageShopLocal.description}
            </h3>
            <div
              style={{
                padding: "20px 0px",
                textAlign: "left",
                fontSize: "16px",
                height: "calc(100% - 180px)",
                overflow: "hidden",
                maxWidth: "95%",
              }}
            >
              {participatingMemberCompaniesTable}
            </div>
            {sortedMembersData.length > 0 && (
              <>
                <CSVLink
                  data={[
                    [
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .memberCompanyName,
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .participatingCode,
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .usersInPrizeDraw,
                    ],
                    ...(sortedMembersData || []).map((member) => {
                      if (!member) return ["", "", ""];

                      const participantEmails = getParticipantEmailsForMember(
                        member.participationCode || ""
                      );

                      return [
                        member.memberName || "",
                        member.participationCode || "",
                        participantEmails.length > 0
                          ? `Total users: ${
                              participantEmails.length
                            }\n\n${participantEmails
                              .map((email) => `${email || ""}`)
                              .join("\n")}`
                          : member.numberOfParticipants || 0,
                      ];
                    }),
                  ]}
                  filename={`Shop Local Campaign Participating Member Companies.csv`}
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <Button
                    className={classes.modalButton}
                    style={{
                      backgroundColor: "#37474f",
                      color: "#e9e9e9",
                      marginBottom: "-5px",
                    }}
                    variant="contained"
                  >
                    {
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .downloadCsv
                    }
                  </Button>
                </CSVLink>
              </>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {memberModalOpenedIsActive && (
                <Button
                  className={classes.modalButton}
                  onClick={() =>
                    history.push(RedirectUri.updateShopLocal, {
                      shopLocalCampaignId: memberModalOpenedForCampaignsId,
                    })
                  }
                  variant="contained"
                >
                  {enumStrings.fullPageShopLocal.updateMemberList}
                </Button>
              )}
              <Button
                className={classes.modalButton}
                style={{
                  backgroundColor: "#37474f",
                  color: "#e9e9e9",
                }}
                onClick={() => setParticipatingMembersModalOpen(false)}
                variant="contained"
              >
                {enumStrings.shopLocal.createShopLocalCampaign.close}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {participantModalOpen && (
        <Modal className={classes.modal} open={participantModalOpen}>
          <div
            className={classes.paper}
            style={{ justifyContent: "space-between", height: "800px" }}
          >
            <h1 style={{ fontWeight: "bold" }}>
              {
                enumStrings.shopLocal.fullPageShopLocalCampaign
                  .chamberPerksAppUsersTitle
              }
              <br />
              {
                enumStrings.shopLocal.fullPageShopLocalCampaign
                  .participatingInPrizeDraw
              }
            </h1>
            <div
              style={{
                padding: "20px 0px",
                textAlign: "left",
                fontSize: "16px",
                height: "calc(100% - 180px)",
                overflow: "hidden",
                maxWidth: "95%",
              }}
            >
              {cpaUsersParticipatingInDrawList}
            </div>
            {memberModalOpenedForCampaignsId &&
              Boolean(
                allShopLocalCampaignData?.find(
                  (campaign) => campaign.id === memberModalOpenedForCampaignsId
                )?.prizeDrawParticipants?.length
              ) && (
                <CSVLink
                  data={[
                    [
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .entrantEmail,
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .enteredPrizeDrawByRedeeming,
                    ],
                    ...(sortPrizeDrawParticipantsByRedemptions(
                      (allShopLocalCampaignData || []).find(
                        (campaign) =>
                          campaign.id === memberModalOpenedForCampaignsId
                      )?.prizeDrawParticipants || []
                    ).map((participant) => {
                      // Convert member IDs to names with bullet points
                      const memberNames = participant.redeemedAtMemberIds
                        .map((id) => {
                          const member =
                            allChamberMemberData?.getAllChamberMembers?.find(
                              (m) => m.id === id
                            );
                          return member
                            ? `${member.name}`
                            : `Unknown Member (${id})`;
                        })
                        .join("\n");

                      return [participant.userEmail, memberNames];
                    }) || []),
                  ]}
                  filename={`Prize Draw Participants.csv`}
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <Button
                    className={classes.modalButton}
                    style={{
                      backgroundColor: "#37474f",
                      color: "#e9e9e9",
                      marginBottom: "-5px",
                    }}
                    variant="contained"
                  >
                    {
                      enumStrings.shopLocal.fullPageShopLocalCampaign
                        .downloadCsv
                    }
                  </Button>
                </CSVLink>
              )}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                flexWrap: "wrap",
                maxWidth: "80%",
              }}
            >
              <Button
                className={classes.modalButton}
                onClick={() => {
                  setRandomWinnerEmailFunc();
                  setRandomWinnerModalOpen(true);
                }}
                variant="contained"
              >
                {
                  enumStrings.shopLocal.fullPageShopLocalCampaign
                    .pickRandomWinner
                }
              </Button>
              <Button
                className={classes.modalButton}
                style={{
                  backgroundColor: "#37474f",
                  color: "#e9e9e9",
                }}
                onClick={() => setParticipantModalOpen(false)}
                variant="contained"
              >
                {enumStrings.shopLocal.createShopLocalCampaign.close}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {randomWinnerModalOpen && (
        <Modal className={classes.modal} open={randomWinnerModalOpen}>
          <div
            className={classes.paper}
            style={{ justifyContent: "space-between", height: "300px" }}
          >
            <h1 style={{ fontWeight: "bold" }}>
              {enumStrings.fullPageShopLocal.randomWinnerIs}
            </h1>
            <div
              style={{
                padding: "50px",
                paddingBottom: 0,
                paddingTop: 20,
                textAlign: "center",
                fontSize: "16px",
                height: "100%",
                overflowY: "scroll",
                whiteSpace: "pre-wrap",
              }}
            >
              {randomWinnerEmail}
            </div>
            <br />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                className={classes.modalButton}
                style={{
                  backgroundColor: "#37474f",
                  color: "#e9e9e9",
                }}
                onClick={() => setRandomWinnerModalOpen(false)}
                variant="contained"
              >
                {enumStrings.shopLocal.createShopLocalCampaign.close}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {isMobile ? (() => {
        const q = mobileSearch.trim().toLowerCase();
        const filtered = q
          ? allTableShopLocalCampaignData.filter((row) => {
              const haystack = [row.campaignName || "", row.prizeDescription || ""].join(" ").toLowerCase();
              return haystack.includes(q);
            })
          : allTableShopLocalCampaignData;
        const menuCampaign = filtered.find((c) => c.id === mobileMenuCampaignId);
        return (
          <div className={classes.mobileList}>
            <TextField
              className={classes.mobileSearchBar}
              variant="outlined"
              size="small"
              placeholder="Search by Contest Name or Prize Description"
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#666", fontSize: "18px" }} />
                  </InputAdornment>
                ),
              }}
            />
            {filtered.length === 0 ? (
              <div className={classes.mobileEmpty}>{emptyMessage}</div>
            ) : (
              filtered.map((row) => {
                const isActive = row.status === ShopLocalCampaignStatusEnum.Active;
                const isUpcoming = isActive && Date.now() < row.startDateUtcMilli;
                const statusLabel = !isActive
                  ? enumStrings.shopLocal.fullPageShopLocalCampaign.ended
                  : isUpcoming
                  ? enumStrings.shopLocal.fullPageShopLocalCampaign.upcoming
                  : enumStrings.shopLocal.fullPageShopLocalCampaign.active;
                const pillClass = !isActive
                  ? classes.mobileStatusPill
                  : `${classes.mobileStatusPill} ${isUpcoming ? classes.mobileStatusPillUpcoming : classes.mobileStatusPillActive}`;
                const count = (row?.prizeDrawParticipants || []).length;
                const participatingCount = (row?.participatingMembers || []).length;
                return (
                  <div key={row.id} className={classes.mobileCard}>
                    <div className={classes.mobileCardTop}>
                      <div className={classes.mobileAvatar}>{getInitials(row.campaignName || "")}</div>
                      <div className={classes.mobileTitles}>
                        <div className={classes.mobileCampaignTitle}>{row.campaignName}</div>
                        {row.prizeDescription ? (
                          <div className={classes.mobileCampaignSubtitle}>{row.prizeDescription}</div>
                        ) : null}
                      </div>
                      <span className={pillClass}>{statusLabel}</span>
                      {isActive && (
                        <IconButton
                          size="small"
                          className={classes.mobileMenuBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileMenuAnchor(e.currentTarget);
                            setMobileMenuCampaignId(row.id);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                    <div className={classes.mobileDivider} />
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.fullPageShopLocal.prizeDescription}</span>
                      <span className={classes.mobileMetaValue}>{row.prizeDescription || "-"}</span>
                    </div>
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.fullPageShopLocal.participatingMembers}</span>
                      <span className={classes.mobileMetaValue}>{participatingCount}</span>
                    </div>
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.fullPageShopLocal.startDate}</span>
                      <span className={classes.mobileMetaValue}>{formatShortDate(row.startDateUtcMilli || 0)}</span>
                    </div>
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.fullPageShopLocal.endDate}</span>
                      <span className={classes.mobileMetaValue}>{formatShortDate(row.endDateUtcMilli || 0)}</span>
                    </div>
                    <div className={classes.mobileDivider} />
                    <div className={classes.mobileBottomRow}>
                      <div>
                        <div className={classes.mobileCountNumber}>{count}</div>
                        <div className={classes.mobileCountLabel}>
                          {enumStrings.fullPageShopLocal.prizeDrawParticipants}
                        </div>
                      </div>
                      {count > 0 && (
                        <Button
                          className={classes.mobileViewUsersBtn}
                          onClick={() => {
                            setMemberModalOpenedForCampaignsId(row.id);
                            setParticipantModalOpen(true);
                          }}
                        >
                          {isActive
                            ? enumStrings.shopLocal.fullPageShopLocalCampaign.openListAndPickWinner
                            : enumStrings.shopLocal.fullPageShopLocalCampaign.openList}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor) && !!menuCampaign}
              onClose={() => {
                setMobileMenuAnchor(null);
                setMobileMenuCampaignId(null);
              }}
              PaperProps={{ style: { borderRadius: "10px", minWidth: "160px" } }}
            >
              <MenuItem
                onClick={() => {
                  const id = mobileMenuCampaignId;
                  setMobileMenuAnchor(null);
                  setMobileMenuCampaignId(null);
                  if (id) {
                    history.push(RedirectUri.updateShopLocal, { shopLocalCampaignId: id });
                  }
                }}
                style={{ fontSize: "14px", padding: "10px 16px" }}
              >
                {enumStrings.shopLocal.fullPageShopLocalCampaign.edit}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const id = mobileMenuCampaignId;
                  setMobileMenuAnchor(null);
                  setMobileMenuCampaignId(null);
                  if (id) {
                    setEndModalCampaignId(id);
                    setEndCampaignModalOpen(true);
                  }
                }}
                style={{ fontSize: "14px", padding: "10px 16px", color: "#C62828" }}
              >
                {enumStrings.shopLocal.fullPageShopLocalCampaign.end}
              </MenuItem>
            </Menu>
          </div>
        );
      })() : (
      <CommonTable<TableShopLocalCampaignDataType>
        data={allTableShopLocalCampaignData}
        columns={columns}
        defaultSortColumn="startDateUtcMilli"
        defaultSortOrder="desc"
        rowKeyField="id"
        renderCell={renderCell}
        onRowClick={mode === "active" ? handleRowClick : undefined}
        emptyMessage={emptyMessage}
        searchPlaceholder="Search by Contest Name or Prize Description"
        searchFields={["campaignName", "prizeDescription"]}
      />
      )}
    </>
  );
}
